/**
 * RoofMapperProvider
 * Configuration provider for RoofMapper component
 */

import React, { ReactNode } from 'react';
import { MapProvider } from '../contexts/MapContext';
import { SolarProvider } from '../contexts/SolarContext';
import { RoofMapperProviderInternal } from './contexts/RoofMapperContext';
import type {
  RoofMapperConfig,
  RoofMapperTheme,
  RoofMapperFeatures,
  LocationData,
  SolarData,
} from './types';
import { defaultTheme, defaultFeatures } from './types';

interface RoofMapperProviderProps {
  children: ReactNode;
  config: RoofMapperConfig;
  onLocationSelect?: (location: LocationData) => void;
  onSolarDataFetched?: (data: SolarData) => void;
  onError?: (error: Error) => void;
}

export function RoofMapperProvider({
  children,
  config,
  onLocationSelect,
  onSolarDataFetched,
  onError,
}: RoofMapperProviderProps) {
  // Merge user config with defaults
  const theme: RoofMapperTheme = {
    ...defaultTheme,
    ...config.theme,
    colors: {
      ...defaultTheme.colors,
      ...config.theme?.colors,
    },
    fonts: {
      ...defaultTheme.fonts,
      ...config.theme?.fonts,
    },
    spacing: {
      ...defaultTheme.spacing,
      ...config.theme?.spacing,
    },
    shadows: {
      ...defaultTheme.shadows,
      ...config.theme?.shadows,
    },
    borderRadius: {
      ...defaultTheme.borderRadius,
      ...config.theme?.borderRadius,
    },
  };

  const features: RoofMapperFeatures = {
    ...defaultFeatures,
    ...config.enabledFeatures,
  };

  return (
    <RoofMapperProviderInternal
      config={config}
      theme={theme}
      features={features}
      onLocationSelect={onLocationSelect}
      onSolarDataFetched={onSolarDataFetched}
      onError={onError}
    >
      <MapProvider>
        <SolarProvider>
          {children}
        </SolarProvider>
      </MapProvider>
    </RoofMapperProviderInternal>
  );
}

export default RoofMapperProvider;
